import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ProjectTeam() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ user_id: '', project_role_id: '', allocation_percentage: '100', start_date: '', end_date: '', is_active: true });
  const [editId, setEditId] = useState(null);
  const [projectRoles, setProjectRoles] = useState([]);
  const [workload, setWorkload] = useState(null);

  const load = useCallback(() => {
    if (!companyId || !projectId) return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/projects/${projectId}/team`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setMembers(Array.isArray(d) ? d : []))
      .catch(() => setMembers([])).finally(() => setLoading(false));
  }, [companyId, projectId, API]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/projects-settings/roles`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setProjectRoles(Array.isArray(d) ? d : [])).catch(() => {});
  }, [companyId, API]);

  const loadWorkload = (userId) => {
    fetch(`${API}/companies/${companyId}/projects/${projectId}/team/workload/${userId}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null).then(d => setWorkload(d)).catch(() => {});
  };

  const handleSave = () => {
    const method = editId ? 'PUT' : 'POST';
    const url = editId ? `${API}/companies/${companyId}/projects/${projectId}/team/${editId}` : `${API}/companies/${companyId}/projects/${projectId}/team`;
    const body = { ...form };
    if (!body.end_date) delete body.end_date;
    fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(r => {
        if (!r.ok) return r.json().then(err => { throw new Error(err.message || 'Failed'); });
        return r.json();
      })
      .then(() => { setShowModal(false); setEditId(null); resetForm(); load(); })
      .catch(e => window.alert(e.message || 'Failed to save'));
  };

  const resetForm = () => setForm({ user_id: '', project_role_id: '', allocation_percentage: '100', start_date: '', end_date: '', is_active: true });

  const handleRemove = (id) => {
    if (!window.confirm('Remove this team member?')) return;
    fetch(`${API}/companies/${companyId}/projects/${projectId}/team/${id}`, { method: 'DELETE', headers: getAuthHeaders() })
      .then(r => {
        if (!r.ok) return r.json().then(err => { throw new Error(err.message || 'Failed'); });
        load();
      })
      .catch(e => window.alert(e.message || 'Cannot remove'));
  };

  const openEdit = (m) => {
    setEditId(m.id);
    setForm({
      user_id: m.user_id || '',
      project_role_id: m.project_role_id || '',
      allocation_percentage: m.allocation_percentage || '100',
      start_date: m.start_date || '',
      end_date: m.end_date || '',
      is_active: m.is_active !== false,
    });
    setShowModal(true);
  };

  if (!companyId) return <div style={pageStyle}><p>Please select a company.</p></div>;

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <button onClick={() => navigate(`/company/projects/${projectId}`)} style={linkBtn}>← Back to Project</button>
          <h2 style={{ margin: '4px 0 0', fontSize: '1.4rem', fontWeight: 700 }}>👥 Team & Assignments</h2>
        </div>
        <button onClick={() => { setEditId(null); resetForm(); setShowModal(true); }} style={primaryBtn}>+ Add Member</button>
      </div>

      {initialLoading ? <p>Loading...</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Member</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Allocation</th>
                <th style={thStyle}>Period</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => {
                const isOverAllocated = (m.total_allocation || 0) > 100;
                return (
                  <tr key={m.id}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{m.user?.name || 'Unknown'}</div>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{m.user?.email || ''}</div>
                    </td>
                    <td style={tdStyle}>{m.project_role?.name || '-'}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: '60px', height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px' }}>
                          <div style={{ width: `${Math.min(m.allocation_percentage || 0, 100)}%`, height: '100%', backgroundColor: isOverAllocated ? '#ef4444' : '#3b82f6', borderRadius: '3px' }} />
                        </div>
                        <span style={{ fontSize: '0.8rem', color: isOverAllocated ? '#ef4444' : '#334155', fontWeight: isOverAllocated ? 700 : 400 }}>{m.allocation_percentage || 0}%</span>
                      </div>
                      {isOverAllocated && <div style={{ fontSize: '0.65rem', color: '#ef4444' }}>⚠ Over-allocated ({m.total_allocation}% total)</div>}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: '0.8rem' }}>{m.start_date || '-'} → {m.end_date || 'Ongoing'}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ ...badge, backgroundColor: m.is_active ? '#22c55e' : '#94a3b8' }}>{m.is_active ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => openEdit(m)} style={smallBtn}>Edit</button>
                        <button onClick={() => loadWorkload(m.user_id)} style={smallBtn}>Workload</button>
                        <button onClick={() => handleRemove(m.id)} style={{ ...smallBtn, color: '#ef4444' }}>Remove</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {members.length === 0 && (
                <tr><td colSpan="6" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No team members assigned</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Workload Panel */}
      {workload && (
        <div style={{ ...cardStyle, marginTop: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>📊 Workload Summary — {workload.user?.name || 'User'}</h3>
            <button onClick={() => setWorkload(null)} style={{ background: 'none', border: 'none', fontSize: '1rem', cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px', marginBottom: '12px' }}>
            <div style={statCard}><div style={statLabel}>Total Allocation</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: (workload.total_allocation || 0) > 100 ? '#ef4444' : '#3b82f6' }}>{workload.total_allocation || 0}%</div></div>
            <div style={statCard}><div style={statLabel}>Active Projects</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#3b82f6' }}>{workload.active_projects || 0}</div></div>
          </div>
          {Array.isArray(workload.assignments) && workload.assignments.length > 0 && (
            <table style={tableStyle}>
              <thead><tr><th style={thStyle}>Project</th><th style={thStyle}>Role</th><th style={thStyle}>Allocation</th></tr></thead>
              <tbody>
                {workload.assignments.map((a, i) => (
                  <tr key={i}><td style={tdStyle}>{a.project?.name || '-'}</td><td style={tdStyle}>{a.project_role?.name || '-'}</td><td style={tdStyle}>{a.allocation_percentage}%</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px' }}>{editId ? 'Edit Member' : 'Add Team Member'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {!editId && (
                <div>
                  <label style={labelStyle}>User ID</label>
                  <input type="number" placeholder="User ID" value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })} style={inputStyle} />
                </div>
              )}
              <div>
                <label style={labelStyle}>Project Role</label>
                <select value={form.project_role_id} onChange={e => setForm({ ...form, project_role_id: e.target.value })} style={inputStyle}>
                  <option value="">Select Role</option>
                  {projectRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Allocation %</label>
                <input type="number" min="0" max="100" value={form.allocation_percentage} onChange={e => setForm({ ...form, allocation_percentage: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}><label style={labelStyle}>Start</label><input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} style={inputStyle} /></div>
                <div style={{ flex: 1 }}><label style={labelStyle}>End</label><input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} style={inputStyle} /></div>
              </div>
              {editId && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} /> Active
                </label>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowModal(false)} style={cancelBtn}>Cancel</button>
              <button onClick={handleSave} style={primaryBtn}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1400px', margin: '0 auto' };
const cardStyle = { backgroundColor: '#fff', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const statCard = { backgroundColor: '#fff', borderRadius: '8px', padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const statLabel = { fontSize: '0.75rem', color: '#64748b' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' };
const tdStyle = { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };
const badge = { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', color: '#fff', fontWeight: 600, textTransform: 'capitalize' };
const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
const labelStyle = { fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px' };
const primaryBtn = { padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' };
const cancelBtn = { padding: '8px 16px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' };
const smallBtn = { padding: '4px 8px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' };
const linkBtn = { background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.85rem', padding: 0 };
const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px', maxHeight: '85vh', overflowY: 'auto' };
